import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getGetVenuaPlannerSerieDetailQueryKey,
  getGetVenuaPlannerSerieEconomicsQueryKey,
  useGenerateVenuaPlannerTerms,
  useGetVenuaPlannerSerieDetail,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { cn } from '@/shared/lib/utils';
import { Button, Dialog, Icon, Input, Panel, toast } from '@/shared/ui';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/shadcn';

import { plannerQueryKeys } from '../../../plannerApi';
import { RecurrenceSettings } from './components/RecurrenceSettings';
import { TermCalendarPicker } from './components/TermCalendarPicker';
import { TermPreviewTable } from './components/TermPreviewTable';
import {
  addDays,
  generateTerms,
  GEN_WARN,
  isoOf,
  parseIso,
  type TermGenMode,
  type TermRecurrence,
  type TermSpan,
} from './termGenerator';
import { toGenerateTermsRequest } from './toGenerateTermsRequest';

interface GenerateTermsDialogProps {
  productId: string;
  serieId: string;
  onClose: () => void;
}

function dniLabel(days: number): string {
  if (days === 1) return '1 den';
  if (days >= 2 && days <= 4) return `${days} dny`;
  return `${days} dní`;
}

function nightsLabel(nights: number): string {
  if (nights === 1) return '1 noc';
  if (nights >= 2 && nights <= 4) return `${nights} noci`;
  return `${nights} nocí`;
}

/**
 * Dialog „Generování termínů" – běžný modální dialog (mimo navigaci). Levý sloupec drží pravidla, pravý
 * živý náhled; po „Vygenerovat" zavolá bulk command, invaliduje detail i strom a zavře se.
 */
export function GenerateTermsDialog({ productId, serieId, onClose }: GenerateTermsDialogProps) {
  const { t } = useTranslation('planner');
  const queryClient = useQueryClient();

  const detailQuery = useGetVenuaPlannerSerieDetail(productId, serieId);
  const generateMutation = useGenerateVenuaPlannerTerms();

  const baseNights = Math.max(0, (detailQuery.data?.mainProgramLengthDays ?? 1) - 1);

  const [mode, setMode] = useState<TermGenMode>('recurrence');
  const [span, setSpan] = useState<TermSpan>('includeEnds');
  const [recurrence, setRecurrence] = useState<TermRecurrence>('days');
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => isoOf(today));
  const [to, setTo] = useState(() => isoOf(addDays(today, 90)));
  const [everyDays, setEveryDays] = useState(1);
  const [everyWeeks, setEveryWeeks] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([5]); // pátek
  const [calendarStarts, setCalendarStarts] = useState<Date[]>([]);
  const [nightsOverride, setNightsOverride] = useState<number | null>(null);
  const [editingLength, setEditingLength] = useState(false);
  const [confirmedUpTo, setConfirmedUpTo] = useState(GEN_WARN);

  const nights = nightsOverride ?? baseNights;

  const result = useMemo(
    () =>
      generateTerms({
        mode,
        nights,
        from: from ? parseIso(from) : null,
        to: to ? parseIso(to) : null,
        span,
        recurrence,
        everyDays,
        everyWeeks,
        daysOfWeek,
        calendarStarts,
      }),
    [mode, nights, from, to, span, recurrence, everyDays, everyWeeks, daysOfWeek, calendarStarts]
  );

  const total = result.terms.length;

  // Re-arming gate: jakmile počet klesne pod měkký limit, strop se vrátí na GEN_WARN.
  useEffect(() => {
    if (total <= GEN_WARN) setConfirmedUpTo(GEN_WARN);
  }, [total]);

  const blocked = total > confirmedUpTo;

  const handleSubmit = useCallback(async () => {
    try {
      const response = await generateMutation.mutateAsync({
        productId,
        serieId,
        data: toGenerateTermsRequest({
          mode,
          span,
          recurrence,
          from,
          to,
          everyDays,
          everyWeeks,
          daysOfWeek,
          calendarStarts,
          nightsOverride,
          confirmedCount: confirmedUpTo,
        }),
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerSerieEconomicsQueryKey(productId, serieId),
        }),
        queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() }),
      ]);
      toast({
        severity: 'success',
        description: t('serieDetail.generateTerms.toast.created', { count: response.createdCount }),
      });
      onClose();
    } catch {
      toast({
        severity: 'error',
        title: t('serieDetail.generateTerms.toast.errorTitle'),
        description: t('serieDetail.generateTerms.toast.error'),
      });
    }
  }, [
    generateMutation,
    productId,
    serieId,
    mode,
    span,
    recurrence,
    from,
    to,
    everyDays,
    everyWeeks,
    daysOfWeek,
    calendarStarts,
    nightsOverride,
    confirmedUpTo,
    queryClient,
    t,
    onClose,
  ]);

  const submitDisabled = generateMutation.isPending || total === 0 || result.truncated || blocked;

  return (
    <Dialog
      open
      onClose={onClose}
      icon="calendar"
      tone="primary"
      title={t('serieDetail.generateTerms.title')}
      subtitle={detailQuery.data ? `· ${detailQuery.data.name}` : undefined}
      closeLabel={t('serieDetail.generateTerms.close')}
      width="xl"
      className="h-[760px] max-h-full max-w-[980px]"
      bodyPadded={false}
      bodyClassName="min-h-0 overflow-hidden"
      footer={
        <>
          {blocked ? (
            <div className="bg-phx-warning-tint border-phx-warning text-phx-warning-fg gap-phx-2 rounded-phx-md text-phx-md flex items-center border px-3 py-2 font-semibold">
              <Icon name="alert" size={15} className="text-phx-warning" />
              <span>{t('serieDetail.generateTerms.gate.warning', { count: total })}</span>
              <Button size="sm" variant="outline" onClick={() => setConfirmedUpTo(total)}>
                {t('serieDetail.generateTerms.gate.allow')}
              </Button>
            </div>
          ) : (
            <span className="text-phx-text-muted text-phx-sm">
              {t('serieDetail.generateTerms.footerNote')}
            </span>
          )}
          <span className="flex-1" />
          <div className="gap-phx-2 flex items-center">
            <Button variant="outline" onClick={onClose}>
              {t('serieDetail.generateTerms.back')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={submitDisabled}
              className={cn(submitDisabled && 'pointer-events-none opacity-50')}
            >
              {t('serieDetail.generateTerms.submit')}
            </Button>
          </div>
        </>
      }
    >
      {/* ── tělo: vlevo pravidla, vpravo živý náhled ── */}
      <div className="grid h-full grid-cols-[1fr_320px] overflow-hidden">
        <div className="gap-phx-5 flex min-h-0 flex-col overflow-y-auto p-4">
          {/* délka termínu – převzatá z nastavení seriálu, přepsatelná (editace se otevře vlevo) */}
          <div className="gap-phx-2 border-phx-border bg-phx-surface-2 rounded-phx-md flex items-center border p-3">
            <Icon name="clock" size={15} className="text-phx-text-muted" />
            {editingLength ? (
              <span className="gap-phx-2 text-phx-sm text-phx-text-2 flex items-center">
                {t('serieDetail.generateTerms.length.label')}
                <Input
                  type="number"
                  size="sm"
                  min={0}
                  max={60}
                  autoFocus
                  defaultValue={nights}
                  onBlur={(event) => {
                    setNightsOverride(Math.max(0, Number(event.target.value) || 0));
                    setEditingLength(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Escape') event.currentTarget.blur();
                  }}
                  className="w-13 text-center"
                />
                <span className="text-phx-text-muted">
                  {t('serieDetail.generateTerms.length.nights')}
                </span>
              </span>
            ) : (
              <span className="text-phx-sm text-phx-text-2">
                {t('serieDetail.generateTerms.length.label')}{' '}
                <b className="text-phx-text font-semibold">
                  {dniLabel(nights + 1)} · {nightsLabel(nights)}
                </b>{' '}
                <span className="text-phx-text-muted">
                  {nightsOverride != null
                    ? t('serieDetail.generateTerms.length.manual')
                    : t('serieDetail.generateTerms.length.fromSeries')}
                </span>
              </span>
            )}
            <span className="flex-1" />
            {nightsOverride != null ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNightsOverride(null);
                  setEditingLength(false);
                }}
                title={t('serieDetail.generateTerms.length.resetTitle')}
              >
                {t('serieDetail.generateTerms.length.reset')}
              </Button>
            ) : null}
            {!editingLength ? (
              <Button size="sm" variant="outline" onClick={() => setEditingLength(true)}>
                {t('serieDetail.generateTerms.length.change')}
              </Button>
            ) : null}
          </div>

          <Panel variant="outline" title={t('serieDetail.generateTerms.mode.title')}>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={mode}
              onValueChange={(value) => value && setMode(value as TermGenMode)}
              className="w-full"
            >
              <ToggleGroupItem value="recurrence" className="flex-1">
                {t('serieDetail.generateTerms.mode.recurrence')}
              </ToggleGroupItem>
              <ToggleGroupItem value="calendar" className="flex-1">
                {t('serieDetail.generateTerms.mode.calendar')}
              </ToggleGroupItem>
            </ToggleGroup>
          </Panel>

          {mode === 'recurrence' ? (
            <RecurrenceSettings
              span={span}
              onSpanChange={setSpan}
              from={from}
              onFromChange={setFrom}
              to={to}
              onToChange={setTo}
              recurrence={recurrence}
              onRecurrenceChange={setRecurrence}
              everyDays={everyDays}
              onEveryDaysChange={setEveryDays}
              everyWeeks={everyWeeks}
              onEveryWeeksChange={setEveryWeeks}
              daysOfWeek={daysOfWeek}
              onDaysOfWeekChange={setDaysOfWeek}
            />
          ) : (
            <Panel variant="outline" title={t('serieDetail.generateTerms.calendar.title')}>
              <TermCalendarPicker selected={calendarStarts} onChange={setCalendarStarts} />
            </Panel>
          )}
        </div>

        <div className="border-phx-border bg-phx-surface-2 flex min-h-0 flex-col border-l">
          <div className="text-phx-text-2 border-phx-border text-phx-xs px-phx-3 border-b py-3 font-semibold tracking-wide uppercase">
            {t('serieDetail.generateTerms.preview.heading')}
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <TermPreviewTable result={result} nights={nights} />
          </div>
          <div className="border-phx-border text-phx-sm px-phx-3 gap-phx-2 flex items-center border-t py-2 font-semibold">
            {t('serieDetail.generateTerms.preview.total')}{' '}
            <span className="text-phx-primary tabular-nums" data-testid="generate-terms-total">
              {total}
              {result.truncated ? '+' : ''}
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
